import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';
import { supabase } from 'lib/supabase';
import ClientDashboardNavbar from 'pages/client-dashboard/components/ClientDashboardNavbar';
import Footer from 'pages/landing-page/components/Footer';

// ─── Google Fonts import ───────────────────────────────────────────────────────
const FONT_LINK_ID = 'volaptic-client-fonts';
const FONT_STYLE_ID = 'volaptic-client-font-style';

const FONTS = [
  { id: 'default', label: 'Default', family: null, description: 'Font di sistema' },
  { id: 'plus-jakarta-sans', label: 'Plus Jakarta Sans', family: "'Plus Jakarta Sans', sans-serif", googleName: 'Plus+Jakarta+Sans:wght@400;500;600;700' },
  { id: 'dm-sans', label: 'DM Sans', family: "'DM Sans', sans-serif", googleName: 'DM+Sans:wght@400;500;600;700' },
  { id: 'outfit', label: 'Outfit', family: "'Outfit', sans-serif", googleName: 'Outfit:wght@400;500;600;700' },
];

const AVATAR_ICONS = [
  { id: 'person', label: 'Persona', svg: (color) => (
    <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="28" height="28">
      <circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/>
    </svg>
  )},
  { id: 'house', label: 'Casa', svg: (color) => (
    <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="28" height="28">
      <path d="M3 9.5L12 3l9 6.5V20a1 1 0 01-1 1H4a1 1 0 01-1-1V9.5z"/><path d="M9 21V12h6v9"/>
    </svg>
  )},
  { id: 'star', label: 'Stella', svg: (color) => (
    <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="28" height="28">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
    </svg>
  )},
  { id: 'heart', label: 'Cuore', svg: (color) => (
    <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="28" height="28">
      <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/>
    </svg>
  )},
  { id: 'lightning', label: 'Fulmine', svg: (color) => (
    <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="28" height="28">
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
    </svg>
  )},
  { id: 'leaf', label: 'Foglia', svg: (color) => (
    <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="28" height="28">
      <path d="M17 8C8 10 5.9 16.17 3.82 19.34a1 1 0 001.38 1.4C7.2 19.1 9.5 17 12 17c4 0 8-3 8-8 0-3-1-5-3-7z"/>
      <path d="M3 21c0-4 2-7 5-9"/>
    </svg>
  )},
  { id: 'smile', label: 'Sorriso', svg: (color) => (
    <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="28" height="28">
      <circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/>
    </svg>
  )},
  { id: 'rocket', label: 'Razzo', svg: (color) => (
    <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="28" height="28">
      <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 00-2.91-.09z"/>
      <path d="M12 15l-3-3a22 22 0 012-3.95A12.88 12.88 0 0122 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 01-4 2z"/>
      <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0"/><path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5"/>
    </svg>
  )},
];

// Inject Google Fonts link tag
const injectGoogleFonts = () => {
  if (document.getElementById(FONT_LINK_ID)) return;
  const fontNames = FONTS?.filter(f => f?.googleName)?.map(f => f?.googleName)?.join('&family=');
  const link = document.createElement('link');
  link.id = FONT_LINK_ID;
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${fontNames}&display=swap`;
  document.head?.appendChild(link);
};

// Apply font to /cliente pages via a style tag
const applyClientFont = (fontId) => {
  let styleEl = document.getElementById(FONT_STYLE_ID);
  if (!styleEl) {
    styleEl = document.createElement('style');
    styleEl.id = FONT_STYLE_ID;
    document.head?.appendChild(styleEl);
  }
  const font = FONTS?.find(f => f?.id === fontId);
  if (font?.family) {
    styleEl.textContent = `body { font-family: ${font?.family} !important; }`;
  } else {
    styleEl.textContent = `body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important; }`;
  }
};

const STORAGE_KEY_FONT = 'volaptic_client_font';
const STORAGE_KEY_AVATAR = 'volaptic_client_avatar';

// ─── Main Page ─────────────────────────────────────────────────────────────────
const ClienteSettings = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  // Profile
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileSuccess, setProfileSuccess] = useState('');
  const [profileError, setProfileError] = useState('');
  const [headerName, setHeaderName] = useState('');

  // Avatar
  const [selectedAvatar, setSelectedAvatar] = useState('person');
  const [avatarSaving, setAvatarSaving] = useState(false);
  const [avatarSuccess, setAvatarSuccess] = useState('');

  // Photo avatar
  const [avatarMode, setAvatarMode] = useState('icon'); // 'icon' or 'photo'
  const [photoUrl, setPhotoUrl] = useState(null);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoError, setPhotoError] = useState('');
  const [photoSuccess, setPhotoSuccess] = useState('');
  const photoInputRef = useRef(null);

  // Font
  const [selectedFont, setSelectedFont] = useState('default');
  const [fontSaving, setFontSaving] = useState(false);
  const [fontSuccess, setFontSuccess] = useState('');

  // Password
  const [passwordForm, setPasswordForm] = useState({ current: '', newPass: '', confirm: '' });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);

  // Address
  const [address, setAddress] = useState('');
  const [addressSaving, setAddressSaving] = useState(false);
  const [addressSuccess, setAddressSuccess] = useState('');
  const [addressError, setAddressError] = useState('');

  // Mapbox autocomplete for address
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [addressLoading, setAddressLoading] = useState(false);
  const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);
  const addressSearchTimeoutRef = useRef(null);
  const addressWrapperRef = useRef(null);

  const MAPBOX_TOKEN = (() => {
    const envToken = import.meta.env?.VITE_MAPBOX_TOKEN;
    if (envToken && envToken !== 'your-mapbox-token-here' && !envToken?.includes('placeholder')) {
      return envToken;
    }
    return 'pk.eyJ1IjoicGVkZXJ6b2xsaXNpbW9uZSIsImEiOiJjbWt5OTA3OHowNG02M2VzZTFoYWJwN3E2In0.HCcTrRyl9L0dWbcIp0x1iQ';
  })();

  const [pageLoading, setPageLoading] = useState(true);

  // Inject Google Fonts on mount
  useEffect(() => {
    injectGoogleFonts();
  }, []);

  // Load persisted font + avatar from localStorage
  useEffect(() => {
    const savedFont = localStorage.getItem(STORAGE_KEY_FONT);
    if (savedFont) {
      setSelectedFont(savedFont);
      applyClientFont(savedFont);
    }
    const savedAvatar = localStorage.getItem(STORAGE_KEY_AVATAR);
    if (savedAvatar) {
      setSelectedAvatar(savedAvatar);
    }
  }, []);

  // Auth guard + data fetch
  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login-form'); return; }

    const loadData = async () => {
      setPageLoading(true);
      try {
        const { data: userData } = await supabase
          ?.from('users')
          ?.select('email, full_name, avatar_icon, avatar_url')
          ?.eq('id', user?.id)
          ?.single();

        if (userData) {
          setEmail(userData?.email || user?.email || '');
          setNome(userData?.full_name || '');
          if (userData?.avatar_url) {
            setPhotoUrl(userData?.avatar_url);
            setAvatarMode('photo');
          } else if (userData?.avatar_icon) {
            setSelectedAvatar(userData?.avatar_icon);
            setAvatarMode('icon');
          }
        } else {
          setEmail(user?.email || '');
        }
      } catch (err) {
        setEmail(user?.email || '');
      } finally {
        setPageLoading(false);
      }
    };

    loadData();
  }, [user, authLoading, navigate]);

  // Load default_city on mount using supabase.auth.getUser()
  useEffect(() => {
    const loadDefaultCity = async () => {
      try {
        const { data: { user: currentUser } } = await supabase?.auth?.getUser();
        if (!currentUser) return;
        const { data } = await supabase?.from('users')?.select('default_city')?.eq('id', currentUser?.id)?.single();
        if (data?.default_city) {
          setAddress(data?.default_city);
        }
      } catch (err) {
        // silently ignore
      }
    };
    loadDefaultCity();
  }, []);

  // Close address suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (addressWrapperRef?.current && !addressWrapperRef?.current?.contains(event?.target)) {
        setShowAddressSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Mapbox geocoding search
  const searchAddressLocations = async (query) => {
    if (!query || query?.length < 2) {
      setAddressSuggestions([]);
      return;
    }
    setAddressLoading(true);
    try {
      const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?country=IT&types=country,region,place,locality&language=it&limit=5&access_token=${MAPBOX_TOKEN}`;
      const response = await fetch(url);
      if (!response?.ok) throw new Error(`Mapbox API returned ${response.status}`);
      const data = await response?.json();
      const transformed = data?.features?.map(feature => ({
        mapbox_id: feature?.id,
        name: feature?.place_name,
      })) || [];
      setAddressSuggestions(transformed);
      setShowAddressSuggestions(true);
    } catch (error) {
      setAddressSuggestions([]);
    } finally {
      setAddressLoading(false);
    }
  };

  const handleAddressInputChange = (e) => {
    const value = e?.target?.value;
    setAddress(value);
    if (addressSearchTimeoutRef?.current) clearTimeout(addressSearchTimeoutRef?.current);
    addressSearchTimeoutRef.current = setTimeout(() => {
      searchAddressLocations(value);
    }, 300);
  };

  const handleSelectAddressSuggestion = (suggestion) => {
    setAddress(suggestion?.name || '');
    setShowAddressSuggestions(false);
    setAddressSuggestions([]);
  };

  // ── Profile save ─────────────────────────────────────────────────────────────
  const handleProfileSave = async (e) => {
    e?.preventDefault();
    setProfileError('');
    setProfileSuccess('');
    setProfileSaving(true);
    try {
      const { error } = await supabase
        ?.from('users')
        ?.update({ full_name: nome })
        ?.eq('id', user?.id);
      if (error) throw error;
      setHeaderName(nome);
      setProfileSuccess('Nome aggiornato');
    } catch (err) {
      setProfileError('Errore durante il salvataggio del profilo');
    } finally {
      setProfileSaving(false);
    }
  };

  // ── Avatar save ──────────────────────────────────────────────────────────────
  const handleAvatarSave = async () => {
    setAvatarSaving(true);
    setAvatarSuccess('');
    try {
      const { data: { user: currentUser } } = await supabase?.auth?.getUser();
      if (!currentUser) throw new Error('Not authenticated');
      const { error } = await supabase
        ?.from('users')
        ?.update({ avatar_icon: selectedAvatar, avatar_url: null })
        ?.eq('id', currentUser?.id);
      if (error) throw error;
      localStorage.setItem(STORAGE_KEY_AVATAR, selectedAvatar);
      setPhotoUrl(null);
      window.dispatchEvent(new CustomEvent('volaptic-avatar-update', {
        detail: { icon: selectedAvatar, url: null }
      }));
      setAvatarSuccess('Avatar salvato');
      setTimeout(() => setAvatarSuccess(''), 3000);
    } catch (err) {
      setAvatarSuccess('');
    } finally {
      setAvatarSaving(false);
    }
  };

  // ── Font save ────────────────────────────────────────────────────────────────
  const handleFontSave = () => {
    setFontSaving(true);
    localStorage.setItem(STORAGE_KEY_FONT, selectedFont);
    applyClientFont(selectedFont);
    setTimeout(() => {
      setFontSaving(false);
      setFontSuccess('Preferenze salvate');
      setTimeout(() => setFontSuccess(''), 3000);
    }, 300);
  };

  // ── Password change ──────────────────────────────────────────────────────────
  const handlePasswordSubmit = async (e) => {
    e?.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    if (passwordForm?.newPass !== passwordForm?.confirm) {
      setPasswordError('Le password non coincidono');
      return;
    }
    if (passwordForm?.newPass?.length < 8) {
      setPasswordError('La nuova password deve essere di almeno 8 caratteri');
      return;
    }

    setPasswordLoading(true);

    const { error: signInError } = await supabase?.auth?.signInWithPassword({
      email: email,
      password: passwordForm?.current,
    });

    if (signInError) {
      setPasswordError('Password attuale non corretta');
      setPasswordLoading(false);
      return;
    }

    const { error: updateError } = await supabase?.auth?.updateUser({ password: passwordForm?.newPass });

    if (updateError) {
      setPasswordError(updateError?.message || "Errore durante l'aggiornamento della password");
    } else {
      setPasswordSuccess('Password aggiornata con successo');
      setPasswordForm({ current: '', newPass: '', confirm: '' });
    }

    setPasswordLoading(false);
  };

  // ── Address save ─────────────────────────────────────────────────────────────
  const handleAddressSave = async (e) => {
    e?.preventDefault();
    setAddressError('');
    setAddressSuccess('');
    setAddressSaving(true);
    try {
      const { data: { user: currentUser } } = await supabase?.auth?.getUser();
      if (!currentUser) {
        setAddressError('Sessione scaduta, effettua di nuovo il login');
        setAddressSaving(false);
        return;
      }
      const { error } = await supabase?.from('users')?.update({ default_city: address })?.eq('id', currentUser?.id);
      if (error) throw error;
      setAddressSuccess('Indirizzo salvato');
    } catch (err) {
      setAddressError('Errore durante il salvataggio');
    } finally {
      setAddressSaving(false);
    }
  };

  const currentAvatarIcon = AVATAR_ICONS?.find(a => a?.id === selectedAvatar) || AVATAR_ICONS?.[0];

  // ── Photo upload handler ─────────────────────────────────────────────────────
  const handlePhotoUpload = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    setPhotoError(''); setPhotoSuccess('');
    if (!['image/jpeg', 'image/png']?.includes(file?.type)) {
      setPhotoError('Formato non supportato. Usa JPEG o PNG.'); return;
    }
    if (file?.size > 2 * 1024 * 1024) {
      setPhotoError('Il file supera i 2MB.'); return;
    }
    setPhotoUploading(true);
    try {
      const path = `client-avatars/${user?.id}.jpg`;
      const { error: upErr } = await supabase?.storage?.from('professional-portfolio')?.upload(path, file, { upsert: true, contentType: file?.type });
      if (upErr) throw upErr;
      const { data: urlData } = supabase?.storage?.from('professional-portfolio')?.getPublicUrl(path);
      const publicUrl = urlData?.publicUrl + '?v=' + Date.now();
      await supabase?.from('users')?.update({ avatar_url: publicUrl, avatar_icon: null })?.eq('id', user?.id);
      setPhotoUrl(publicUrl);
      setSelectedAvatar(null);
      localStorage.removeItem(STORAGE_KEY_AVATAR);
      window.dispatchEvent(new CustomEvent('volaptic-avatar-update', {
        detail: { icon: null, url: publicUrl }
      }));
      setPhotoSuccess('Foto profilo aggiornata!');
      setTimeout(() => setPhotoSuccess(''), 4000);
    } catch (err) {
      console.error('Photo upload error:', err);
      setPhotoError(err?.message || 'Errore durante il caricamento.');
    } finally {
      setPhotoUploading(false);
      if (photoInputRef?.current) photoInputRef.current.value = '';
    }
  };

  const handleRemovePhoto = async () => {
    setPhotoError(''); setPhotoSuccess('');
    try {
      await supabase?.from('users')?.update({ avatar_url: null })?.eq('id', user?.id);
      setPhotoUrl(null);
      window.dispatchEvent(new CustomEvent('volaptic-avatar-update', {
        detail: { icon: selectedAvatar ?? null, url: null }
      }));
      setPhotoSuccess('Foto rimossa.');
      setTimeout(() => setPhotoSuccess(''), 3000);
    } catch (err) {
      setPhotoError('Errore durante la rimozione.');
    }
  };

  if (authLoading || pageLoading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{
          backgroundImage: "url('/assets/images/italian_desk_minimal_warm.png')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          position: 'relative',
        }}
      >
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.55)', zIndex: 0 }} />
        <div className="flex flex-col items-center gap-4" style={{ position: 'relative', zIndex: 1 }}>
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-600">Caricamento impostazioni...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* Main content area with background image + overlay — does NOT extend into footer */}
      <div
        className="pt-16"
        style={{
          flex: 1,
          backgroundImage: "url('/assets/images/italian_desk_minimal_warm.png')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundAttachment: 'fixed',
          position: 'relative',
        }}
      >
        {/* White overlay confined to this content area only */}
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.55)', zIndex: 0, pointerEvents: 'none' }} />

        {/* Navbar above overlay */}
        <div style={{ position: 'relative', zIndex: 10 }}>
          <ClientDashboardNavbar selectedAvatar={selectedAvatar} avatarIcons={AVATAR_ICONS} overrideUserName={headerName || undefined} />
        </div>

        <main className="max-w-[720px] mx-auto px-4 py-10" style={{ position: 'relative', zIndex: 1 }}>
          <h1 className="text-2xl font-bold text-gray-900 mb-8">Impostazioni Account</h1>

          {/* ── SECTION 1: PROFILO ─────────────────────────────────────────────── */}
          <section className="mb-6">
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-5">Il tuo profilo</h2>
              <form onSubmit={handleProfileSave} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
                  <input
                    type="text"
                    value={nome}
                    onChange={(e) => setNome(e?.target?.value)}
                    placeholder="Il tuo nome"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={email}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-500 cursor-not-allowed"
                  />
                </div>

                {profileError && (
                  <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{profileError}</p>
                )}
                {profileSuccess && (
                  <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">{profileSuccess}</p>
                )}

                <button
                  type="submit"
                  disabled={profileSaving}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold text-white transition-colors disabled:opacity-60"
                  style={{ backgroundColor: '#104d98' }}
                >
                  {profileSaving ? 'Salvataggio...' : 'Salva modifiche'}
                </button>
              </form>
            </div>
          </section>

          {/* ── SECTION 2: AVATAR ──────────────────────────────────────────────── */}
          <section className="mb-6">
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-5">Il tuo avatar</h2>
              <p className="text-sm text-gray-500 mb-4">Scegli un'icona che ti rappresenta. Apparirà nel cerchio dell'intestazione.</p>

              {/* Toggle icon / photo */}
              <div className="flex gap-2 mb-5 p-1 bg-gray-100 rounded-lg w-fit">
                <button
                  type="button"
                  onClick={() => setAvatarMode('icon')}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
                    avatarMode === 'icon' ?'bg-white text-[#013776] shadow-sm' :'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Icona
                </button>
                <button
                  type="button"
                  onClick={() => setAvatarMode('photo')}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
                    avatarMode === 'photo' ?'bg-white text-[#013776] shadow-sm' :'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Foto
                </button>
              </div>

              {avatarMode === 'icon' && (
                <>
                  <div className="grid grid-cols-4 gap-3 mb-5">
                    {AVATAR_ICONS?.map((icon) => {
                      const isSelected = selectedAvatar === icon?.id;
                      return (
                        <button
                          key={icon?.id}
                          type="button"
                          onClick={() => setSelectedAvatar(icon?.id)}
                          title={icon?.label}
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '6px',
                            padding: '14px 8px',
                            borderRadius: '12px',
                            border: isSelected ? '2.5px solid #32b3e5' : '1.5px solid #e5e7eb',
                            background: isSelected ? '#f0f9ff' : '#fff',
                            cursor: 'pointer',
                            transition: 'all 0.15s',
                            boxShadow: isSelected ? '0 0 0 3px rgba(50,179,229,0.15)' : 'none',
                          }}
                        >
                          {icon?.svg(isSelected ? '#32b3e5' : '#6b7280')}
                          <span style={{ fontSize: '11px', color: isSelected ? '#32b3e5' : '#6b7280', fontWeight: isSelected ? 600 : 400 }}>
                            {icon?.label}
                          </span>
                        </button>
                      );
                    })}
                  </div>

                  {avatarSuccess && (
                    <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2 mb-3">{avatarSuccess}</p>
                  )}

                  <button
                    type="button"
                    onClick={handleAvatarSave}
                    disabled={avatarSaving}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold text-white transition-colors disabled:opacity-60"
                    style={{ backgroundColor: '#104d98' }}
                  >
                    {avatarSaving ? 'Salvataggio...' : 'Salva avatar'}
                  </button>
                </>
              )}

              {avatarMode === 'photo' && (
                <div>
                  <p className="text-sm text-gray-500 mb-4">
                    Carica una foto profilo. JPEG o PNG, max 2MB.
                  </p>
                  <div className="flex items-center gap-5 mb-4">
                    {photoUrl ? (
                      <img
                        src={photoUrl}
                        alt="Avatar"
                        style={{ width: '96px', height: '96px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0, boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}
                      />
                    ) : (
                      <div style={{
                        width: '96px', height: '96px', borderRadius: '50%',
                        background: 'linear-gradient(135deg, #32b3e5 0%, #013776 100%)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0,
                      }}>
                        <span style={{ color: '#fff', fontWeight: 700, fontSize: '28px' }}>
                          {(headerName || user?.email || '?')?.slice(0, 1)?.toUpperCase()}
                        </span>
                      </div>
                    )}
                    <div className="flex flex-col gap-2">
                      <input
                        ref={photoInputRef}
                        type="file"
                        accept="image/jpeg,image/png"
                        style={{ display: 'none' }}
                        onChange={handlePhotoUpload}
                      />
                      <button
                        type="button"
                        onClick={() => photoInputRef?.current?.click()}
                        disabled={photoUploading}
                        className="px-4 py-2 bg-[#2780b5] text-white rounded-lg hover:bg-[#013776] transition-colors text-sm font-medium disabled:opacity-50"
                      >
                        {photoUploading ? 'Caricamento...' : (photoUrl ? 'Cambia foto' : 'Carica foto')}
                      </button>
                      {photoUrl && (
                        <button
                          type="button"
                          onClick={handleRemovePhoto}
                          className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors text-sm font-medium"
                        >
                          Rimuovi foto
                        </button>
                      )}
                    </div>
                  </div>
                  {photoError && (
                    <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-3">
                      {photoError}
                    </p>
                  )}
                  {photoSuccess && (
                    <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2 mb-3">
                      {photoSuccess}
                    </p>
                  )}
                </div>
              )}
            </div>
          </section>

          {/* ── SECTION 3: ASPETTO ─────────────────────────────────────────────── */}
          <section className="mb-6">
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-5">Aspetto</h2>
              <p className="text-sm text-gray-500 mb-4">Scegli il font per le pagine cliente.</p>
              <div className="space-y-3 mb-5">
                {FONTS?.map((font) => {
                  const isSelected = selectedFont === font?.id;
                  return (
                    <label
                      key={font?.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '14px',
                        padding: '14px 16px',
                        borderRadius: '10px',
                        border: isSelected ? '2px solid #32b3e5' : '1.5px solid #e5e7eb',
                        background: isSelected ? '#f0f9ff' : '#fff',
                        cursor: 'pointer',
                        transition: 'all 0.15s',
                      }}
                    >
                      <input
                        type="radio"
                        name="font-picker"
                        value={font?.id}
                        checked={isSelected}
                        onChange={() => setSelectedFont(font?.id)}
                        style={{ accentColor: '#32b3e5', width: '16px', height: '16px', flexShrink: 0 }}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>
                          {font?.label}
                          {font?.description && (
                            <span style={{ fontWeight: 400, color: '#9ca3af', marginLeft: '6px', fontSize: '12px' }}>
                              — {font?.description}
                            </span>
                          )}
                        </div>
                        <div
                          style={{
                            fontFamily: font?.family || '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                            fontSize: '15px',
                            color: isSelected ? '#32b3e5' : '#6b7280',
                            fontWeight: 400,
                          }}
                        >
                          Testo di esempio
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>

              {fontSuccess && (
                <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2 mb-3">{fontSuccess}</p>
              )}

              <button
                type="button"
                onClick={handleFontSave}
                disabled={fontSaving}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold text-white transition-colors disabled:opacity-60"
                style={{ backgroundColor: '#104d98' }}
              >
                {fontSaving ? 'Salvataggio...' : 'Salva preferenze'}
              </button>
            </div>
          </section>

          {/* ── SECTION 4: PASSWORD ────────────────────────────────────────────── */}
          <section className="mb-6">
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-5">Modifica password</h2>
              <form onSubmit={handlePasswordSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Password attuale</label>
                  <input
                    type="password"
                    required
                    value={passwordForm?.current}
                    onChange={(e) => setPasswordForm((prev) => ({ ...prev, current: e?.target?.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nuova password</label>
                  <input
                    type="password"
                    required
                    minLength={8}
                    value={passwordForm?.newPass}
                    onChange={(e) => setPasswordForm((prev) => ({ ...prev, newPass: e?.target?.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Conferma nuova password</label>
                  <input
                    type="password"
                    required
                    value={passwordForm?.confirm}
                    onChange={(e) => setPasswordForm((prev) => ({ ...prev, confirm: e?.target?.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {passwordError && (
                  <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{passwordError}</p>
                )}
                {passwordSuccess && (
                  <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">{passwordSuccess}</p>
                )}

                <button
                  type="submit"
                  disabled={passwordLoading}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold text-white transition-colors disabled:opacity-60"
                  style={{ backgroundColor: '#104d98' }}
                >
                  {passwordLoading ? 'Aggiornamento...' : 'Aggiorna password'}
                </button>
              </form>
            </div>
          </section>

          {/* ── SECTION 5: INDIRIZZO ───────────────────────────────────────────── */}
          <section className="mb-6">
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-5">Indirizzo principale</h2>
              <form onSubmit={handleAddressSave} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Città o Località</label>
                  <div className="relative" ref={addressWrapperRef}>
                    <input
                      type="text"
                      value={address}
                      onChange={handleAddressInputChange}
                      placeholder="Es: Milano, Roma, Torino"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />

                    {/* Loading indicator */}
                    {addressLoading && (
                      <div className="absolute right-3 top-2.5">
                        <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                      </div>
                    )}

                    {/* Suggestions dropdown */}
                    {showAddressSuggestions && addressSuggestions?.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                        {addressSuggestions?.map((suggestion, index) => (
                          <button
                            key={suggestion?.mapbox_id || index}
                            type="button"
                            onClick={() => handleSelectAddressSuggestion(suggestion)}
                            className="w-full text-left px-4 py-3 hover:bg-gray-100 transition-colors border-b border-gray-100 last:border-b-0"
                          >
                            <div className="flex items-start gap-2">
                              <svg viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16" className="mt-0.5 flex-shrink-0">
                                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
                              </svg>
                              <span className="text-sm text-gray-800">{suggestion?.name}</span>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}

                    {/* No results */}
                    {showAddressSuggestions && !addressLoading && addressSuggestions?.length === 0 && address?.length >= 2 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg p-4 text-center text-sm text-gray-500">
                        Nessuna località trovata
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-1.5">Verrà usato come predefinito nelle nuove richieste.</p>
                </div>

                {addressError && (
                  <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{addressError}</p>
                )}
                {addressSuccess && (
                  <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">{addressSuccess}</p>
                )}

                <button
                  type="submit"
                  disabled={addressSaving}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold text-white transition-colors disabled:opacity-60"
                  style={{ backgroundColor: '#104d98' }}
                >
                  {addressSaving ? 'Salvataggio...' : 'Salva indirizzo'}
                </button>
              </form>
            </div>
          </section>

          {/* ── FOOTER: DELETE ACCOUNT ─────────────────────────────────────────── */}
          <div className="text-center py-4">
            <p className="text-sm text-gray-400">
              Vuoi eliminare il tuo account?{' '}
              <a
                href="mailto:contact@volaptic.com"
                style={{ color: '#dc2626', textDecoration: 'underline', fontWeight: 500 }}
              >
                Scrivici a contact@volaptic.com
              </a>
            </p>
          </div>
          <Link
            to="/cliente/dashboard"
            className="flex items-center justify-center gap-2 w-full max-w-[720px] mx-auto mt-8 py-3 rounded-xl border border-gray-300 bg-white text-sm font-semibold text-gray-700 hover:bg-gray-50 hover:border-gray-400 shadow-sm transition-colors"
          >
            ← Torna alla dashboard
          </Link>
        </main>
      </div>
      {/* Footer with its own solid white background — fully separate from the bg image area */}
      <div style={{ backgroundColor: '#ffffff' }}>
        <Footer />
      </div>
    </div>
  );
};

export default ClienteSettings;
